import { useState } from 'react';
import { HoldingsBankAccount } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X, Link2 } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' }
];

const BANK_PROVIDERS = [
  { value: '', label: 'Manual Import Only' },
  { value: 'mercury', label: 'Mercury' }
];

interface BankAccountFormProps {
  account?: HoldingsBankAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function BankAccountForm({ account, onClose, onSaved }: BankAccountFormProps) {
  const isEditing = !!account;

  const [name, setName] = useState(account?.name || '');
  const [accountType, setAccountType] = useState(account?.accountType || 'checking');
  const [lastFour, setLastFour] = useState(account?.lastFour || '');
  const [bankName, setBankName] = useState(account?.bankName || '');
  const [isActive, setIsActive] = useState(account?.isActive !== false);
  const [notes, setNotes] = useState(account?.notes || '');

  // Mercury integration
  const [provider, setProvider] = useState((account as any)?.provider || '');
  const [mercuryApiKey, setMercuryApiKey] = useState('');
  const [mercuryAccountId, setMercuryAccountId] = useState((account as any)?.mercuryAccountId || '');
  const [showApiKey, setShowApiKey] = useState(false);
  const isMercuryConnected = !!(account as any)?.mercuryAccountId;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Account name is required');
      return;
    }

    if (lastFour && lastFour.length !== 4) {
      setError('Last 4 digits must be exactly 4 characters');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload: any = {
        name: name.trim(),
        accountType,
        lastFour: lastFour || null,
        bankName: bankName.trim() || null,
        isActive,
        notes: notes.trim() || null,
        provider: provider || null,
        mercuryAccountId: provider === 'mercury' ? mercuryAccountId.trim() || null : null
      };

      // Only send API key if it's been entered (don't overwrite with empty)
      if (provider === 'mercury' && mercuryApiKey.trim()) {
        payload.mercuryApiKey = mercuryApiKey.trim();
      }

      const url = isEditing
        ? `${API_URL}/holdings/bank-accounts/${account.id}`
        : `${API_URL}/holdings/bank-accounts`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save account');
      }
    } catch (e) {
      setError('Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Bank Account' : 'Add Bank Account'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Account Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Business Checking"
              required
            />
          </div>

          {/* Account Type & Bank Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Account Type
              </label>
              <Select value={accountType} onChange={(e) => setAccountType(e.target.value as any)}>
                {ACCOUNT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Last 4 Digits
              </label>
              <Input
                value={lastFour}
                onChange={(e) => setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                maxLength={4}
              />
            </div>
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Bank Name
            </label>
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g., Chase, Bank of America, Mercury"
            />
          </div>

          {/* API Integration */}
          <div className="border-t border-neutral-100 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="w-4 h-4 text-neutral-500" />
              <label className="text-sm font-medium text-neutral-700">
                API Integration (Optional)
              </label>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-neutral-600 mb-1">
                  Provider
                </label>
                <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
                  {BANK_PROVIDERS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </Select>
              </div>

              {provider === 'mercury' && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-blue-800 font-medium">Mercury API Integration</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Connect to Mercury to automatically sync transactions.
                        {isMercuryConnected && (
                          <span className="ml-2 inline-flex items-center gap-1 text-emerald-600">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                            Connected
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-neutral-700 mb-1">
                      Mercury Account ID *
                    </label>
                    <Input
                      value={mercuryAccountId}
                      onChange={(e) => setMercuryAccountId(e.target.value)}
                      placeholder="e.g., 12345678-abcd-1234-efgh-123456789abc"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      Find this in your Mercury URL: app.mercury.com/accounts/<strong>YOUR-ID-HERE</strong>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-neutral-700 mb-1">
                      API Key {isMercuryConnected ? '(leave blank to keep existing)' : '*'}
                    </label>
                    <div className="relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={mercuryApiKey}
                        onChange={(e) => setMercuryApiKey(e.target.value)}
                        placeholder={isMercuryConnected ? '••••••••••••••••' : 'Enter your Mercury API key'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-500 hover:text-neutral-700"
                      >
                        {showApiKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      <a href="https://app.mercury.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Create a read-only API token
                      </a>
                      {' '}in Mercury Settings
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-neutral-300"
              />
              <span className="font-medium text-neutral-700">Active</span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Account'} />
          </div>
        </form>
      </div>
    </div>
  );
}
